/**
 * useComprehensionLogic Hook
 *
 * Manages COMPREHENSION question logic using payload.blanks structure.
 * Works directly with npm-pikado-logic's raw API format (NO customChoices abstraction).
 */

import {useState, useCallback} from 'react';
import type {QuestionWithNewFields} from '../../Questions/QuestionTypes';
import {
  detectChoicesKeywords,
  initializeEmptyBlanks,
  replaceChoicesInText,
  duplicateChoicesKeywordsByWordCount,
  validateChoicesKeywordsExist,
  cleanStringForAI,
} from '../utils/comprehensionParser';

export interface UseComprehensionLogicReturn {
  // Selected blank index for editing
  selectedBlankIndex: number;
  setSelectedBlankIndex: (index: number) => void;

  // Alert tracking for validation
  shownChoicesAlerts: Set<number>;

  // Operations
  handleTextChange: (
    questionIndex: number,
    newText: string,
    questions: QuestionWithNewFields[],
    isAIText?: boolean,
  ) => QuestionWithNewFields[];

  handleTextBlur: (
    questionIndex: number,
    questions: QuestionWithNewFields[],
  ) => QuestionWithNewFields[];

  handleCorrectAnswerChange: (
    questionIndex: number,
    blankIndex: number,
    correctAnswer: string,
    questions: QuestionWithNewFields[],
  ) => QuestionWithNewFields[];

  handleChoiceValueChange: (
    questionIndex: number,
    blankIndex: number,
    choiceIndex: number,
    newValue: string,
    questions: QuestionWithNewFields[],
  ) => QuestionWithNewFields[];

  handleAddChoice: (
    questionIndex: number,
    blankIndex: number,
    questions: QuestionWithNewFields[],
  ) => QuestionWithNewFields[];

  initializeComprehensionQuestion: (
    question: QuestionWithNewFields,
  ) => QuestionWithNewFields;
}

/**
 * Custom hook for managing COMPREHENSION question logic with payload.blanks
 */
export const useComprehensionLogic = (): UseComprehensionLogicReturn => {
  const [selectedBlankIndex, setSelectedBlankIndex] = useState<number>(0);
  const [shownChoicesAlerts, setShownChoicesAlerts] = useState<Set<number>>(
    new Set(),
  );

  /**
   * Initializes a NEW COMPREHENSION question with CHOICES keywords from text.
   * Creates payload.blanks structure.
   */
  const initializeComprehensionQuestion = useCallback(
    (question: QuestionWithNewFields): QuestionWithNewFields => {
      const updatedQuestion = {...question};

      if (!updatedQuestion.text) {
        // No text yet, initialize empty payload
        updatedQuestion.payload = {blanks: [], media: []};
        return updatedQuestion;
      }

      // Detect CHOICES keywords from text
      const detectedKeywords = detectChoicesKeywords(updatedQuestion.text, '');

      if (detectedKeywords.length === 0) {
        // No CHOICES found, initialize empty payload
        updatedQuestion.payload = {blanks: [], media: []};
        return updatedQuestion;
      }

      // Initialize empty blanks for detected keywords
      const blanks = initializeEmptyBlanks(detectedKeywords);

      // Initialize payload
      updatedQuestion.payload = {
        blanks,
        media: updatedQuestion.payload?.media || [],
      };

      // Set the first blank as selected
      setSelectedBlankIndex(0);

      return updatedQuestion;
    },
    [],
  );

  /**
   * Handles text input change for questions.
   */
  const handleTextChange = useCallback(
    (
      questionIndex: number,
      newText: string,
      questions: QuestionWithNewFields[],
      isAIText: boolean = false,
    ): QuestionWithNewFields[] => {
      const updatedQuestions = [...questions];
      const question = updatedQuestions[questionIndex];

      if (!isAIText) {
        // Update main text
        question.text = newText;

        if (question.type === 'COMPREHENSION') {
          // Do NOT overwrite textAI with the raw passage here — that would leave
          // literal CHOICES tokens in AiText. The answer-filled textAI is
          // regenerated from text + blanks on blur / correct-answer change.
        } else {
          // For other question types, clean the text for AI
          question.textAI = cleanStringForAI(newText);
        }
      } else {
        // Update AI text directly (for NORMAL_SCORE questions)
        question.textAI = newText;
      }

      return updatedQuestions;
    },
    [],
  );

  /**
   * Handles text blur event to detect and initialize CHOICES keywords.
   * Creates new blanks for NEW keywords, preserves existing blanks.
   */
  const handleTextBlur = useCallback(
    (
      questionIndex: number,
      questions: QuestionWithNewFields[],
    ): QuestionWithNewFields[] => {
      const updatedQuestions = [...questions];
      const question = updatedQuestions[questionIndex];

      if (question.type !== 'COMPREHENSION') {
        // For non-COMPREHENSION questions, clear payload.blanks
        if (!question.payload) question.payload = {blanks: [], media: []};
        question.payload.blanks = [];
        return updatedQuestions;
      }

      // Detect CHOICES keywords in the text
      const detectedKeywords = detectChoicesKeywords(
        question.text || '',
        question.textAI || '',
      );

      // Validate that at least one CHOICES keyword exists
      if (detectedKeywords.length === 0) {
        if (!shownChoicesAlerts.has(questionIndex)) {
          alert('Must Text or AI Text Contains at least one keyword CHOICES1');
          setShownChoicesAlerts(new Set(shownChoicesAlerts).add(questionIndex));
        }
      } else {
        // Remove from shown alerts if user has fixed the issue
        if (shownChoicesAlerts.has(questionIndex)) {
          const newAlerts = new Set(shownChoicesAlerts);
          newAlerts.delete(questionIndex);
          setShownChoicesAlerts(newAlerts);
        }
      }

      // Initialize payload if it doesn't exist
      if (!question.payload) {
        question.payload = {blanks: [], media: []};
      }
      if (!question.payload.blanks) {
        question.payload.blanks = [];
      }

      // Rebuild blanks from the CHOICES keywords currently present in the text:
      // - reuse existing blank data (choices / correctAnswer) when a keyword stays
      // - create empty blanks for newly added keywords
      // - drop blanks whose keyword was removed from the text
      const existingBlanks = question.payload.blanks || [];
      const existingByName = new Map(existingBlanks.map(b => [b.name, b]));

      const updatedBlanks = detectedKeywords.map(
        keyword =>
          existingByName.get(keyword) || {
            name: keyword,
            choices: ['', ''],
            correctAnswer: '',
          },
      );

      question.payload.blanks = updatedBlanks;

      // Regenerate the answer-filled textAI from the current passage + blanks so
      // editing the passage can never leave stale/placeholder content in AiText.
      // text stays the placeholder version (CHOICES tokens); textAI gets the
      // correctAnswer filled in for every blank that has one selected.
      question.text = duplicateChoicesKeywordsByWordCount(
        question.text || '',
        updatedBlanks,
      );
      question.textAI = replaceChoicesInText(
        question.text || '',
        updatedBlanks,
      );

      // Keep the selected blank index within range after pruning.
      if (selectedBlankIndex >= updatedBlanks.length) {
        setSelectedBlankIndex(0);
      }

      return updatedQuestions;
    },
    [shownChoicesAlerts, selectedBlankIndex],
  );

  /**
   * Handles correct answer selection (user selects from dropdown or input).
   */
  const handleCorrectAnswerChange = useCallback(
    (
      questionIndex: number,
      blankIndex: number,
      correctAnswer: string,
      questions: QuestionWithNewFields[],
    ): QuestionWithNewFields[] => {
      const updatedQuestions = [...questions];
      const question = updatedQuestions[questionIndex];

      // Ensure payload exists
      if (!question.payload || !question.payload.blanks) {
        return updatedQuestions;
      }

      // Update the correct answer for this blank
      question.payload.blanks[blankIndex].correctAnswer = correctAnswer;

      // Get the original text to update the main text field
      const originalText = question.text || '';

      // Update the main text (cleanup duplicate CHOICES keywords)
      question.text = duplicateChoicesKeywordsByWordCount(
        originalText,
        question.payload.blanks,
      );

      // Update textAI (replace CHOICES keywords with correct answers)
      question.textAI = replaceChoicesInText(
        originalText,
        question.payload.blanks,
      );

      return updatedQuestions;
    },
    [],
  );

  /**
   * Handles choice value input change (editing a choice option).
   */
  const handleChoiceValueChange = useCallback(
    (
      questionIndex: number,
      blankIndex: number,
      choiceIndex: number,
      newValue: string,
      questions: QuestionWithNewFields[],
    ): QuestionWithNewFields[] => {
      const updatedQuestions = [...questions];
      const question = updatedQuestions[questionIndex];

      // Ensure payload exists
      if (!question.payload || !question.payload.blanks) {
        return updatedQuestions;
      }

      const blank = question.payload.blanks[blankIndex];

      // Ensure choices array has enough slots
      while (blank.choices.length <= choiceIndex) {
        blank.choices.push('');
      }

      // Keep the selected correct answer in sync if the edited choice was the
      // one marked correct, so the checkbox stays on the right choice.
      const previousValue = blank.choices[choiceIndex];
      if (blank.correctAnswer && blank.correctAnswer === previousValue) {
        blank.correctAnswer = newValue;
      }

      // Update the choice value
      blank.choices[choiceIndex] = newValue;

      return updatedQuestions;
    },
    [],
  );

  /**
   * Handles adding a new choice option (max 4 per blank).
   */
  const handleAddChoice = useCallback(
    (
      questionIndex: number,
      blankIndex: number,
      questions: QuestionWithNewFields[],
    ): QuestionWithNewFields[] => {
      const updatedQuestions = [...questions];
      const question = updatedQuestions[questionIndex];

      // Ensure payload exists
      if (!question.payload || !question.payload.blanks) {
        return updatedQuestions;
      }

      const blank = question.payload.blanks[blankIndex];

      // Only allow up to 4 choices
      if (blank.choices.length < 4) {
        blank.choices.push('');
      }

      return updatedQuestions;
    },
    [],
  );

  return {
    selectedBlankIndex,
    setSelectedBlankIndex,
    shownChoicesAlerts,
    handleTextChange,
    handleTextBlur,
    handleCorrectAnswerChange,
    handleChoiceValueChange,
    handleAddChoice,
    initializeComprehensionQuestion,
  };
};
